import React, { useRef, useState } from 'react';
import Card from './Card';
import Button from './Button';
import AnswerDetails from './AnswerDetails';
import { renderMathHtml } from '../../utils/renderMath';

interface ReportDetailsModalProps {
  open: boolean;
  onClose: () => void;
  exam: any;
  attempt: any;
}

const MIN_WIDTH = 400;
const MAX_WIDTH = 0.95 * window.innerWidth;

const ReportDetailsModal: React.FC<ReportDetailsModalProps> = ({ open, onClose, exam, attempt }) => {
  const [width, setWidth] = useState(900);
  const resizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(900);

  const handleMouseDown = (e: React.MouseEvent) => {
    resizing.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = 'ew-resize';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!resizing.current) return;
    let newWidth = startWidth.current + (startX.current - e.clientX);
    newWidth = Math.max(MIN_WIDTH, Math.min(newWidth, MAX_WIDTH));
    setWidth(newWidth);
  };

  const handleMouseUp = () => {
    resizing.current = false;
    document.body.style.cursor = '';
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  if (!open || !attempt || !attempt.report) return null;
  const details = attempt.report.details || [];
  // Compute per-part statistics (counts)
  const partStats: Record<string, { total: number; correct: number; wrong: number }> = details.reduce((acc: any, q: any) => {
    const p = ((q?.part || 'A').toString().toUpperCase());
    if (!acc[p]) acc[p] = { total: 0, correct: 0, wrong: 0 };
    acc[p].total += 1;
    if (q.status === 'correct') acc[p].correct += 1;
    else if (q.status === 'wrong') acc[p].wrong += 1;
    return acc;
  }, {});

  // Compute per-part marks summary and percentage
  const partMarks: Record<string, { obtained: number; total: number; percent: number | null }> = details.reduce((acc: any, q: any) => {
    const p = ((q?.part || 'A').toString().toUpperCase());
    if (!acc[p]) acc[p] = { obtained: 0, total: 0, percent: null };
    const obtained = typeof q.marks_obtained === 'number' ? q.marks_obtained : 0;
    const max = typeof q.max_marks === 'number'
      ? q.max_marks
      : typeof q.total_marks === 'number'
        ? q.total_marks
        : typeof q.marks === 'number'
          ? q.marks
          : 0;
    acc[p].obtained += obtained;
    acc[p].total += max;
    return acc;
  }, {} as Record<string, { obtained: number; total: number; percent: number | null }>);
  Object.keys(partMarks).forEach((p) => {
    const t = partMarks[p];
    t.percent = t.total > 0 ? (t.obtained / t.total) * 100 : null;
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="bg-white rounded-l-lg shadow-lg p-6 h-full fixed right-0 top-0 overflow-y-auto transition-transform duration-300"
        style={{ width: width + 'px', transform: open ? 'translateX(0)' : 'translateX(100%)', height: '100vh' }}
      >
        {/* Resize handle */}
        <div
          style={{ position: 'absolute', left: 0, top: 0, width: 8, height: '100%', cursor: 'ew-resize', zIndex: 10 }}
          onMouseDown={handleMouseDown}
        />
        {/* <button
          className="absolute top-2 right-2 text-gray-600 hover:text-gray-600 text-3xl leading-none p-1 transition"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button> */}
        {/* <button
    className="absolute top-2 right-2 text-gray-600 hover:text-red-500 text-3xl leading-none p-1 transition"
    onClick={onClose}
    aria-label="Close"
>
    &times;
</button> */}

<button
    className="absolute top-2 right-2 text-gray-600 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-black focus:ring-opacity-50 text-3xl leading-none p-1 transition"
    onClick={onClose}
    aria-label="Close"
>
    &times;
</button>
        <h2 className="text-lg font-semibold mb-2">Detailed Report for {exam.exam_title} (Attempt {attempt.attempt_number})</h2>
        <div className="mb-2 text-gray-900 font-medium">
          Score: {attempt.score_percentage}% | Marks: {attempt.obtained_marks} / {attempt.max_marks} | Correct: {attempt.correct_answers} / {attempt.total_questions}
        </div>
        {/* Part-wise summary */}
        <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {['A', 'B'].map((p) => (
            <div key={p} className="border border-gray-200 rounded-md p-3 bg-gray-50">
              <div className="text-sm font-semibold text-gray-900">Part {p}</div>
              <div className="mt-1 text-xs text-gray-600 flex flex-wrap gap-3">
                <span>Total Qs: <b>{partStats[p]?.total || 0}</b></span>
                <span className="text-green-700">Correct: <b>{partStats[p]?.correct || 0}</b></span>
                <span className="text-red-700">Wrong: <b>{partStats[p]?.wrong || 0}</b></span>
              </div>
              <div className="mt-1 text-xs text-gray-600">
                Marks: <b>{(partMarks[p]?.obtained || 0)}</b> / <b>{(partMarks[p]?.total || 0)}</b>
                {typeof partMarks[p]?.percent === 'number' && (
                  <span> ({partMarks[p]!.percent!.toFixed(2)}%)</span>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="overflow-x-auto mb-6">
          <table className="min-w-full bg-white border border-gray-200 rounded">
            <thead>
              <tr>
                <th className="px-4 py-2 border-b text-left">#</th>
                <th className="px-4 py-2 border-b text-left">Part</th>
                <th className="px-4 py-2 border-b text-left">Type</th>
                <th className="px-4 py-2 border-b text-left">Question</th>
                <th className="px-4 py-2 border-b text-left">Status</th>
                <th className="px-4 py-2 border-b text-left">Marks</th>
              </tr>
            </thead>
            <tbody>
              {details.map((q: any, idx: number) => (
                <tr key={q.question_id || idx} className="border-b last:border-b-0">
                  <td className="px-4 py-2">{idx + 1}</td>
                  <td className="px-4 py-2">{(q.part || 'A').toString().toUpperCase()}</td>
                  <td className="px-4 py-2">{q.question_type}</td>
                  <td className="px-4 py-2"><div className="exam-content" dangerouslySetInnerHTML={{ __html: renderMathHtml(q.question_content) }} /></td>
                  <td className="px-4 py-2">
                    <span className={
                      q.status === 'correct'
                        ? 'text-green-600 font-bold'
                        : q.status === 'wrong'
                          ? 'text-red-600 font-bold'
                          : q.status === 'partially_correct'
                            ? 'text-yellow-600 font-bold'
                            : 'text-gray-600'
                    }>
                      {q.status ? q.status.charAt(0).toUpperCase() + q.status.slice(1).replace('_', ' ') : 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-2">{q.marks_obtained}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Render detailed answers for each question */}
        {details.map((q: any, idx: number) => (
          <div key={q.question_id || idx} className="mb-8">
            <AnswerDetails question={q} />
          </div>
        ))}
        <div className="flex justify-end mt-4">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default ReportDetailsModal; 